import { Soundboard as SoundboardIcon, Plus } from '@/components/ui/icons';
import { useEffect, useRef, useState } from 'react';
import { BUILTIN_SOUNDS, type ServerSoundDTO } from '@clanfire/shared';
import { blazeApi, serverApi } from '@/api/endpoints';
import { Popover } from '@/components/ui/Popover';
import { useMyPermissions } from '@/lib/permissions';
import { cn } from '@/lib/utils';
import { getSocket } from '@/realtime/socket';
import { useHasBlaze } from '@/stores/blaze';
import { useServers } from '@/stores/servers';
import { useVoice } from '@/stores/realtime';
import { toast, useUi } from '@/stores/ui';
import { useVoiceSettings } from '@/stores/voiceSettings';

/** Each server's own sounds, fetched the first time its soundboard opens. */
const cache = new Map<string, ServerSoundDTO[]>();

/** Forget a server's sounds (after they change in Server Settings). */
export const forgetServerSounds = (serverId: string) => cache.delete(serverId);

/** The soundboard: sounds anyone in the voice channel can play for everyone there. */
export function SoundboardButton({ serverId, className }: { serverId: string; className?: string }) {
  const anchor = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        ref={anchor}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label="Soundboard"
        title="Soundboard"
        className={cn('flex items-center justify-center rounded-full transition-colors', open ? 'bg-active text-fg' : 'text-fg-muted hover:bg-hover hover:text-fg', className)}
      >
        <SoundboardIcon size={18} />
      </button>
      {open && <SoundboardPopover serverId={serverId} anchor={anchor} onClose={() => setOpen(false)} />}
    </>
  );
}

function SoundboardPopover({ serverId, anchor, onClose }: { serverId: string; anchor: React.RefObject<HTMLElement | null>; onClose: () => void }) {
  const [sounds, setSounds] = useState<ServerSoundDTO[] | null>(cache.get(serverId) ?? null);
  const [cooldown, setCooldown] = useState(false);
  const muted = useVoice((s) => s.muted || s.deafened);
  const volume = useVoiceSettings((s) => s.soundboardVolume);
  const canManage = useMyPermissions(serverId).can('MANAGE_SERVER');
  const openModal = useUi((s) => s.openModal);
  // With Blaze: your other servers' sounds too.
  const hasBlaze = useHasBlaze();
  const [elsewhere, setElsewhere] = useState<ServerSoundDTO[]>([]);
  const serverNames = useServers((s) => s.servers);
  useEffect(() => {
    if (!hasBlaze) return;
    blazeApi
      .sounds()
      .then((list) => setElsewhere(list.filter((sound) => sound.serverId !== serverId)))
      .catch(() => undefined);
  }, [hasBlaze, serverId]);

  useEffect(() => {
    if (cache.has(serverId)) return;
    serverApi
      .sounds(serverId)
      .then((list) => {
        cache.set(serverId, list);
        setSounds(list);
      })
      .catch(() => setSounds([]));
  }, [serverId]);

  const play = (soundId: string) => {
    if (muted) return toast.info('Unmute yourself to use the soundboard.');
    if (cooldown) return;
    getSocket()?.emit('voice:sound', { soundId });
    // The server allows two every five seconds; a short pause here keeps it friendly.
    setCooldown(true);
    setTimeout(() => setCooldown(false), 2500);
  };

  const tiles = [...BUILTIN_SOUNDS.map((s) => ({ id: s.id, name: s.name, emoji: s.emoji as string | null })), ...(sounds ?? [])];

  return (
    <Popover open onClose={onClose} anchor={anchor} placement="top" label="Soundboard" className="w-[min(320px,calc(100vw-16px))] p-3">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-bold">Soundboard</h2>
        {canManage && (
          <button
            type="button"
            onClick={() => {
              onClose();
              openModal({ type: 'serverSettings', serverId, section: 'soundboard' });
            }}
            className="flex items-center gap-1 text-xs font-semibold text-link hover:underline"
          >
            <Plus size={12} /> Add sounds
          </button>
        )}
      </div>
      {muted && <p className="mb-2 rounded-lg bg-warning/15 px-2 py-1.5 text-xs text-warning">Unmute yourself to play sounds.</p>}
      <div className="grid max-h-60 grid-cols-3 gap-1.5 overflow-y-auto">
        {tiles.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => play(t.id)}
            disabled={cooldown || muted}
            className="flex flex-col items-center gap-0.5 rounded-xl border border-line bg-sunken px-1 py-2 text-center transition-colors enabled:hover:border-accent enabled:hover:bg-accent-soft disabled:opacity-50"
          >
            <span className="text-xl leading-none" aria-hidden>
              {t.emoji || '🔊'}
            </span>
            <span className="w-full truncate text-[11px] font-semibold">{t.name}</span>
          </button>
        ))}
        {elsewhere.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => play(t.id)}
            disabled={cooldown || muted}
            title={`From ${serverNames[t.serverId]?.name ?? 'another server'}`}
            className="flex flex-col items-center gap-0.5 rounded-xl border border-[#f97316]/40 bg-sunken px-1 py-2 text-center transition-colors enabled:hover:border-accent enabled:hover:bg-accent-soft disabled:opacity-50"
          >
            <span className="text-xl leading-none" aria-hidden>
              {t.emoji || '🔊'}
            </span>
            <span className="w-full truncate text-[11px] font-semibold">{t.name}</span>
            <span className="w-full truncate text-[10px] text-fg-muted">{serverNames[t.serverId]?.name}</span>
          </button>
        ))}
      </div>
      <label className="mt-3 flex items-center gap-2 text-xs text-fg-muted">
        <span className="shrink-0">Volume</span>
        <input
          type="range"
          min={0}
          max={100}
          value={volume}
          onChange={(e) => useVoiceSettings.getState().update({ soundboardVolume: Number(e.target.value) })}
          className="min-w-0 flex-1 accent-[var(--clanfire-accent)]"
          aria-label="Soundboard volume"
        />
        <span className="w-8 text-right tabular-nums">{volume}%</span>
      </label>
      <p className="mt-1 text-[11px] text-fg-muted">Everyone in the voice channel hears what you play.</p>
    </Popover>
  );
}
